import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import {
    Form,
    Input,
    Button,
    Collapse,
    Divider,
    Typography,
    DatePicker,
    Table,
    Radio,
    Checkbox,
    Select,
    Space,
    message
} from 'antd';
import {
    FaUser,
    FaFileAlt,
    FaCaretRight
} from 'react-icons/fa';
import dayjs from 'dayjs';
import { useAuth } from "../../../context/AuthContext";
import { useDispatch, useSelector } from 'react-redux';
import {
    EmployeesContainer,
    MainCard,
    CustomCollapse,
    PanelHeader,
    PanelIcon,
    SectionForm,
    FormDivider,
    SubmitButton,
    StyledTable
} from '../styledComp';
import { postComplianceInfo, getComplianceInfo, clearComplianceData, updateSectionStatus } from '../../../redux/slice/hrFormSlice';
import PdfCompliance from './PdfCompliance'
import { trackFeatureAction } from '../../../analytics/googleAnalytics';

const COMPLIANCE_SECTION_NAMES = {
    1: "EPF Nomination",
    2: "EPS Family Particulars",
    3: "Employee Statement",
    4: "PF Details",
    5: "Gratuity Nomination",
};

const { Title, Text } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

const ALPHA_NUMERIC_REGEX = /^[a-zA-Z0-9\s.,'"\-:;!?()@#%&*+/=]+$/;
const NAME_REGEX = /^[A-Za-z\s]{1,50}$/;
const NUMBER_REGEX = /^[0-9]{1,50}$/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PAN_REGEX = /[A-Z]{5}[0-9]{4}[A-Z]{1}/;
const PHONE_REGEX = /^[6-9]\d{9}$/;
const AADHAAR_REGEX = /^[2-9]{1}[0-9]{11}$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const Compliance = forwardRef(({ user_id, searchTrigger, jwt_payload_group }, ref) => {

    if (user_id === null) return null;
    const [activeKey, setActiveKey] = useState(null);
    const [form] = Form.useForm();
    const [form1] = Form.useForm();
    const [form2] = Form.useForm();
    const [form3] = Form.useForm();
    const [form4] = Form.useForm();
    const [form5] = Form.useForm();
    const [currentMaritalStatus, setCurrentMaritalStatus] = useState(null);
    const [isFatherHusband, setIsFatherHusband] = useState(false);
    const [parentsDeceased, setParentsDeceased] = useState(false);
    const [isPreviousEPFMember, setIsPreviousEPFMember] = useState(false);

    const handlePreviousEPFMemberChange = (e) => {
  const value = e.target.value;
  setIsPreviousEPFMember(value === 'Yes');
  
  if (value === 'No') {
    sectionForms[3].setFieldsValue({
      universalKyc: '',
      previousPFKyc: '',
      previousExitDate: null,
      schemeCertNo: '',
      ppoNo: ''
    });
  }
};

    const sectionForms = useMemo(() => {
        return [form1, form2, form3, form4, form5];
    }, []);

    useImperativeHandle(ref, () => ({
        resetAllForms: () => {
            console.log('Resetting forms...');

            // Reset main form
            form.resetFields();

            // Reset all section forms
            sectionForms.forEach((formInstance) => {
                formInstance.resetFields();
            });

            // Reset all dynamic table data states
            setEpfNomineeData([
                { key: 0, name: '', address: '', relationship: '', dob: null, share: '', guardian: '' }
            ]);
            setEpsFamilyData([
                { key: 0, name: '', address: '', dob: null, relationship: '' }
            ]);
            setEpsNomineeData([
                { key: 0, nomineeNameAddress: '', nomineeDob: null, nomineeRelationship: '' }
            ]);
            setGratuityNomineeData([
                { key: 0, name: '', relationship: '', age: '', proportion: '' }
            ]);

            // Reset other states
            setCurrentMaritalStatus(null);
            setParentsDeceased(false);
            setIsFatherHusband(null)

            message.success("Forms have been reset.");
        }
    }));

    const [loadingStates, setLoadingStates] = useState({
        section1: false,
        section2: false,
        section3: false,
        section4: false,
        section5: false
    });

    const [isInternationalWorker, setIsInternationalWorker] = useState(false);

    const { authState } = useAuth();
    const dispatch = useDispatch();
    const jwtBearer = authState.token;
    const mainData = useSelector((state) => state?.hrForm?.compliance_data || {});

    const [epfNomineeData, setEpfNomineeData] = useState(
        mainData.epfNomineeData?.length ?
            mainData.epfNomineeData.map(item => ({
                ...item,
                dob: item.dob ? dayjs(item.dob) : null
            })) :
            [{ key: 0, name: '', address: '', relationship: '', dob: null, share: '', guardian: '' }]
    );

    const [epsFamilyData, setEpsFamilyData] = useState(
        mainData.epsFamilyData?.length ?
            mainData.epsFamilyData.map(item => ({
                ...item,
                dob: item.dob ? dayjs(item.dob) : null
            })) :
            [{ key: 0, name: '', address: '', dob: null, relationship: '' }]
    );

    const [epsNomineeData, setEpsNomineeData] = useState(
        mainData.epsNomineeData?.length ?
            mainData.epsNomineeData.map(item => ({
                ...item,
                nomineeDob: item.nomineeDob ? dayjs(item.nomineeDob) : null
            })) :
            [{ key: 0, nomineeNameAddress: '', nomineeDob: null, nomineeRelationship: '' }]
    );

    const [gratuityNomineeData, setGratuityNomineeData] = useState(
        mainData.gratuityNomineeData || [{ key: 0, name: '', relationship: '', age: '', proportion: '' }]
    );

const formatDateForDisplay = (date) => {
    if (!date) return null;
    if (dayjs.isDayjs(date)) return date;
    
    if (typeof date === 'string') {
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
            return dayjs(date, 'DD/MM/YYYY');
        }
        if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
            return dayjs(date, 'DD-MM-YYYY');
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return dayjs(date);
        }
        return dayjs(date).isValid() ? dayjs(date) : null;
    }
    return null;
};

const formatDateForRequest = (date) => {
    if (!date) return null;
    if (dayjs.isDayjs(date)) return date.format('DD/MM/YYYY');
    
    if (typeof date === 'string') {
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) return date;
        if (/^\d{2}-\d{2}-\d{4}$/.test(date)) {
            return dayjs(date, 'DD-MM-YYYY').format('DD/MM/YYYY');
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return dayjs(date).format('DD/MM/YYYY');
        }
        return dayjs(date).isValid() ? dayjs(date).format('DD/MM/YYYY') : null;
    }
    return null;
};

    const validateInput = (_, value) => {
        if (!value) return Promise.resolve();
        if (!ALPHA_NUMERIC_REGEX.test(value)) {
            return Promise.reject('Special characters like <, >, $ are not allowed');
        }
        return Promise.resolve();
    };

    useEffect(() => {
        trackFeatureAction(
            "MainHrForm",
            "Compliance Form",
            "open"
        );
        }, []);

    useEffect(() => {
        if (user_id === null) {
            dispatch(clearComplianceData());
        } else {
            dispatch(getComplianceInfo({ jwtBearer, user_id }));
        }
    }, [dispatch, jwtBearer, user_id, searchTrigger]);


  useEffect(() => {
        if (mainData) {
            const section1 = mainData.section1 || {};
            const section2 = mainData.section2 || {};
            const section3 = mainData.section3 || {};
            const section4 = mainData.section4 || {};
            const section5 = mainData.section5 || {};

            setIsInternationalWorker(section4.internationalWorker === 'Yes');
            setCurrentMaritalStatus(section1.maritalStatus);
            setIsFatherHusband(section1.isFatherHusband)
            setParentsDeceased(section1.parentsDeceased || false);
            setIsPreviousEPFMember(section4.previousEPFMember === 'No');

            const initialValues = {
                aadhaar: mainData?.aadhaar,
                name: section1.name,
               
                isFatherHusband: section1.isFatherHusband,
                fatherName1: section1.fatherName1,
                spouseName1: section1.spouseName1,
                dob: formatDateForDisplay(section1.dob),
                Kyc: section1.Kyc,
                gender: section1.gender,
                maritalStatus: section1.maritalStatus,
                addressTemp: section1.addressTemp,
                addressPerma: section1.addressPerma,
                dojDate: formatDateForDisplay(section1.dojDate),
                noFamilyDeclaration: !!section1.noFamilyDeclaration,
                dependentParents: !!section1.dependentParents,
                parentsDeceased: !!section1.parentsDeceased,
                memberSignature: section1.memberSignature,
                signatureDate: formatDateForDisplay(section1.signatureDate),

                establishmentNameAddress: section2.establishmentNameAddress,
                epsPlace: section2.epsPlace,
                establishmentDate: formatDateForDisplay(section2.establishmentDate),
                epsDeclaration: !!section2.epsDeclaration,
                // destination: section2.destination,


                employeeName: section3.employeeName,
                statementGender: section3.statementGender,
                religion: section3.religion,
                statementMaritalStatus: section3.statementMaritalStatus,
                department: section3.department,
                postWithTicketNo: section3.postWithTicketNo,
                appointmentDate: formatDateForDisplay(section3.appointmentDate),
                village: section3.village,
                thana: section3.thana,
                subDivision: section3.subDivision,
                postOffice: section3.postOffice,
                district: section3.district,
                state: section3.state,
                witness1NameAddress: section3.witness1NameAddress || "",
                witness2NameAddress: section3.witness2NameAddress || "",
                witness2Signature: section3.witness2Signature,
                employerRefNo: section3.employerRefNo,
                employerSignature: section3.employerSignature,
                designation: section3.designation,
                establishmentDetails: section3.establishmentDetails,
                date3: formatDateForDisplay(section3.establishmentDate),
                place3: section1.place3,

                memberName: section1.name,
                fatherHusbandName: section4.fatherHusbandName,
                spouseName: section4.spouseName,
                form11Dob: formatDateForDisplay(section4.form11Dob),
                form11Gender: section4.form11Gender,
                form11MaritalStatus: section4.form11MaritalStatus,
                email: section4.email,
                officialEmail: section4.officialEmail,
                mobile: section4.mobile,
                previousEPFMember: section4.previousEPFMember,
                previousEPSMember: section4.previousEPSMember,
                universalKyc: section4.universalKyc,
                previousPFKyc: section4.previousPFKyc,
                previousExitDate: formatDateForDisplay(section4.previousExitDate),
                schemeCertNo: section4.schemeCertNo,
                ppoNo: section4.ppoNo,
                internationalWorker: section4.internationalWorker,
                countryOfOrigin: section4.countryOfOrigin,
                accountNoKyc: section4.accountNoKyc,
                ifscNoKyc: section4.ifscNoKyc,

                pan: section4.pan,
                aadhaarNumber: section4.aadhaarNumber,
                date: formatDateForDisplay(section4.date),
                signature: section4.signature,
                place: section4.place,
                passportNo: section4.passportNo,
                passportValidityFrom: formatDateForDisplay(section4.passportValidityFrom),
                passportValidityTo: formatDateForDisplay(section4.passportValidityTo),
                memberTitle: section4.memberTitle,
                joiningDate: formatDateForDisplay(section4.joiningDate),
                pfNumber: section4.pfNumber,
                uanAllotted: section4.uanAllotted,
                kycStatusNewMember: section4.kycStatusNewMember,
                uanTagged: !!section4.uanTagged,
                kycStatusExistingMember: section4.kycStatusExistingMember,
                employerDate: formatDateForDisplay(section4.employerDate),
                employerSignature: section4.employerSignature,
                undertakingDate: formatDateForDisplay(section4.undertakingDate),
                undertakingPlace: section4.undertakingPlace,
                undertakingSignature: section4.undertakingSignature,

                establishmentName: section5.establishmentName,
                nominatorName: section5.nominatorName,
                gratuityDeclaration1: !!section5.gratuityDeclaration1,
                gratuityDeclaration2: !!section5.gratuityDeclaration2,
                gratuityDeclaration3: !!section5.gratuityDeclaration3,
                gratuityDeclaration4: !!section5.gratuityDeclaration4,
                // gratuityDeclaration5: !!section5.gratuityDeclaration5,
                gratuityDeclaration6: !!section5.gratuityDeclaration6,
                exclusionNoticeDate: formatDateForDisplay(section5.exclusionNoticeDate),
                acknowledgmentDate: formatDateForDisplay(section5.acknowledgmentDate),
                employeeSignature: section5.employeeSignature,
                employerCertDate: formatDateForDisplay(section5.employerCertDate),
                employerCertSignature: section5.employerCertSignature
            };

            sectionForms.forEach(form => form.setFieldsValue(initialValues));

            if (section1.epfNomineeData) {
                setEpfNomineeData(section1.epfNomineeData.map(item => ({
                    ...item,
                    dob: formatDateForDisplay(item.dob)
                })));
            }

            if (section2.epsFamilyData) {
                setEpsFamilyData(section2.epsFamilyData.map(item => ({
                    ...item,
                    dob: formatDateForDisplay(item.dob)
                })));
            }

            if (section2.epsNomineeData) {
                setEpsNomineeData(section2.epsNomineeData.map(item => ({
                    ...item,
                    nomineeDob: formatDateForDisplay(item.nomineeDob)
                })));
            }

            if (section5.gratuityNomineeData) {
                setGratuityNomineeData(section5.gratuityNomineeData);
            }
        }
    }, [mainData?.section1]);

    const handleInternationalWorkerChange = (e) => {
        const value = e.target.value;
        setIsInternationalWorker(value === 'Yes');
        sectionForms[3].setFieldsValue({
            internationalWorker: value,
            ...(value === 'No' ? {
                countryOfOrigin: '',
                passportNo: '',
                passportValidityFrom: null,
                passportValidityTo: null
            } : {})
        });
    };

    const handleMaritalStatusChange = (value) => {
        setCurrentMaritalStatus(value);
        setParentsDeceased(false);

        // Reset dependent fields when marital status changes
        sectionForms[0].setFieldsValue({
            spouseName1: '',
            dependentParents: false
        });
    };

    const getRelationshipOptions = () => {
        if (!currentMaritalStatus) return [];

        if (currentMaritalStatus === 'Unmarried' || currentMaritalStatus === 'Widow' || currentMaritalStatus === 'Widower') {
            return parentsDeceased ? [
                { value: 'Brother', label: 'Brother' },
                { value: 'Sister', label: 'Sister' }
            ] : [
                { value: 'Father', label: 'Father' },
                { value: 'Mother', label: 'Mother' }
            ];
        }

        if (currentMaritalStatus === 'Divorced') {
            return [
                { value: 'Father', label: 'Father (Dependent)' },
                { value: 'Mother', label: 'Mother (Dependent)' },
                { value: 'Son', label: 'Son' },
                { value: 'Daughter', label: 'Daughter' }
            ];
        }

        // Married
        return [
            { value: 'Husband', label: 'Husband' },
            { value: 'Wife', label: 'Wife' },
            // { value: 'Father', label: 'Father (Dependent)' },
            // { value: 'Mother', label: 'Mother (Dependent)' },
            { value: 'Son', label: 'Son' },
            { value: 'Daughter', label: 'Daughter' }
        ];
    };
    
    const isSectionCompleted = (sectionNumber) => {
        return mainData[`is_section${sectionNumber}_completed`] === true;
    };

    const isAllSectionsCompleted = () => {
        return [1, 2, 3, 4, 5].every(
            section => isSectionCompleted(section) && 
            !(jwt_payload_group === '10' || jwt_payload_group === '14' || jwt_payload_group === '18')
        );
    };

    const renderField = (sectionNumber, element) => {
        if (isAllSectionsCompleted()) {
            return React.cloneElement(element, {
                disabled: true,
                ...element.props
            });
        }
        return React.cloneElement(element, {
            disabled: false,
            ...element.props
        });
    };

    const onCollapseChange = (keys) => {
        setActiveKey(keys);
    };

    // Add this function to validate the total percentage
const validateTotalPercentage = () => {
    const totalPercentage = epfNomineeData.reduce((sum, item) => {
        const share = parseFloat(item.share) || 0;
        return sum + share;
    }, 0);
    
    return totalPercentage === 100;
};

    const handleSectionSubmit = async (sectionNumber, values) => {
        setLoadingStates(prev => ({ ...prev, [`section${sectionNumber}`]: true }));

        const formattedValues = {
            ...values,
            dob: formatDateForRequest(values.dob),
            dojDate: formatDateForRequest(values.dojDate),
            form11Dob: formatDateForRequest(values.form11Dob),
            joiningDate: formatDateForRequest(values.joiningDate),
            previousExitDate: formatDateForRequest(values.previousExitDate),
            acknowledgmentDate: formatDateForRequest(values.acknowledgmentDate),
            employerCertDate: formatDateForRequest(values.employerCertDate),
            establishmentDate: formatDateForRequest(values.establishmentDate),
            undertakingDate: formatDateForRequest(values.undertakingDate),
            parentsDeceased: parentsDeceased
        };

        let requestBody = {
            [`is_section${sectionNumber}_completed`]: true
        };

        if (sectionNumber === 1) {
             if (!validateTotalPercentage()) {
            message.error('Total percentage share for EPF nominees must be exactly 100%');
            setLoadingStates(prev => ({ ...prev, section1: false }));
            return;
        }
            requestBody.section1 = {
                ...formattedValues,
                epfNomineeData: epfNomineeData.map(item => ({
                    ...item,
                    dob: formatDateForRequest(item.dob)
                }))
            };
        } else if (sectionNumber === 2) {
            requestBody.section2 = {
                ...formattedValues,
                epsFamilyData: epsFamilyData.map(item => ({
                    ...item,
                    dob: formatDateForRequest(item.dob)
                })),
                epsNomineeData: epsNomineeData.map(item => ({
                    ...item,
                    nomineeDob: formatDateForRequest(item.nomineeDob)
                })),
                establishmentDate: formatDateForRequest(values.establishmentDate)
            };
        } else if (sectionNumber === 3) {
           requestBody.section3 = {
                ...formattedValues,
                witness1NameAddress: values.witness1NameAddress,
                witness2NameAddress: values.witness2NameAddress
            };
        } else if (sectionNumber === 4) {
            requestBody.section4 = { ...formattedValues }
        } else if (sectionNumber === 5) {
            requestBody.section5 = {
                ...formattedValues,
                gratuityNomineeData: gratuityNomineeData
            };
        }

        try {
            const response = await dispatch(postComplianceInfo({
                jwtBearer,
                requestBody,
                section: sectionNumber,
                user_id
            }));

            if (response.error) {
                throw new Error(`Failed to save section ${sectionNumber} information`);
            }

            await dispatch(getComplianceInfo({ jwtBearer, user_id }));
            trackFeatureAction(
                "MainHrForm",
                "Compliance Form",
                "save",
                {
                    section:
                    COMPLIANCE_SECTION_NAMES[sectionNumber] ||
                    `section_${sectionNumber}`,
                }
            );
            message.success(`Section ${sectionNumber} information saved successfully`);
            setActiveKey(null);
        } catch (error) {
            message.error(`Error saving section ${sectionNumber} information`);
            console.error('Error:', error);
        } finally {
            setLoadingStates(prev => ({ ...prev, [`section${sectionNumber}`]: false }));
        }
    };

    const handleEpfNomineeChange = (key, field, value) => {
        if (isAllSectionsCompleted()) return;
        const newData = epfNomineeData.map(item =>
            item.key === key ? { ...item, [field]: value } : item
        );
        setEpfNomineeData(newData);
    };

    const handleEpsFamilyChange = (key, field, value) => {
        if (isAllSectionsCompleted()) return;
        const newData = epsFamilyData.map(item =>
            item.key === key ? { ...item, [field]: value } : item
        );
        setEpsFamilyData(newData);
    };

    const handleEpsNomineeChange = (key, field, value) => {
        if (isAllSectionsCompleted()) return;
        const newData = epsNomineeData.map(item =>
            item.key === key ? { ...item, [field]: value } : item
        );
        setEpsNomineeData(newData);
    };

    const handleGratuityNomineeChange = (key, field, value) => {
        if (isAllSectionsCompleted()) return;
        const newData = gratuityNomineeData.map(item =>
            item.key === key ? { ...item, [field]: value } : item
        );
        setGratuityNomineeData(newData);
    };

    const addEpfNomineeRow = () => {
        if (isAllSectionsCompleted()) return;
        const newKey = epfNomineeData.length > 0 ? Math.max(...epfNomineeData.map(item => item.key)) + 1 : 0;
        setEpfNomineeData([...epfNomineeData, {
            key: newKey,
            name: '',
            address: '',
            relationship: '',
            dob: null,
            share: '',
            guardian: ''
        }]);
    };

    const addEpsFamilyRow = () => {
        if (isAllSectionsCompleted()) return;
        const newKey = epsFamilyData.length > 0 ? Math.max(...epsFamilyData.map(item => item.key)) + 1 : 0;
        setEpsFamilyData([...epsFamilyData, {
            key: newKey,
            name: '',
            address: '',
            dob: null,
            relationship: ''
        }]);
    };

    const addEpsNomineeRow = () => {
        if (isAllSectionsCompleted()) return;
        const newKey = epsNomineeData.length > 0 ? Math.max(...epsNomineeData.map(item => item.key)) + 1 : 0;
        setEpsNomineeData([...epsNomineeData, {
            key: newKey,
            nomineeNameAddress: '',
            nomineeDob: null,
            nomineeRelationship: ''
        }]);
    };

    const addGratuityNomineeRow = () => {
        if (isAllSectionsCompleted()) return;
        const newKey = gratuityNomineeData.length > 0 ? Math.max(...gratuityNomineeData.map(item => item.key)) + 1 : 0;
        setGratuityNomineeData([...gratuityNomineeData, {
            key: newKey,
            name: '',
            relationship: '',
            age: '',
            proportion: ''
        }]);
    };

    const createTableColumn = (dataIndex, title, placeholder = '') => ({
        title,
        dataIndex,
        key: dataIndex,
        render: (text, record) => (
            isAllSectionsCompleted() ?
                <div>{text || '-'}</div> :
                <Input
                    value={text}
                    onChange={(e) => {
                        const handler = {
                            epfNomineeData: handleEpfNomineeChange,
                            epsFamilyData: handleEpsFamilyChange,
                            epsNomineeData: handleEpsNomineeChange,
                            gratuityNomineeData: handleGratuityNomineeChange
                        }[record.tableType];
                        handler?.(record.key, dataIndex, e.target.value);
                    }}
                    placeholder={placeholder}
                />
        )
    });

    const createDateColumn = (dataIndex, title) => ({
        title,
        dataIndex,
        key: dataIndex,
        render: (date, record) => (
            isAllSectionsCompleted() ?
                <div>{date ? dayjs(date).format('DD/MM/YYYY') : '-'}</div> :
                <DatePicker
                    value={date ? dayjs(date) : null}
                    onChange={(date) => {
                        const handler = {
                            epfNomineeData: handleEpfNomineeChange,
                            epsFamilyData: handleEpsFamilyChange,
                            epsNomineeData: handleEpsNomineeChange,
                            gratuityNomineeData: handleGratuityNomineeChange
                        }[record.tableType];
                        handler?.(record.key, dataIndex, date);
                    }}
                    format="DD/MM/YYYY"
                    style={{ width: '100%' }}
                />
        )
    });

    // const epfNomineeColumns = [
    //     createTableColumn('name', 'Name of the Nominee', 'Name'),
    //     createTableColumn('address', 'Address', 'Address'),
    //     {
    //         ...createTableColumn('relationship', 'Relationship', 'Relationship'),
    //         render: (text, record) => (
    //             isAllSectionsCompleted() ?
    //                 <div>{text || '-'}</div> :
    //                 <Select
    //                     value={text}
    //                     onChange={(value) => handleEpfNomineeChange(record.key, 'relationship', value)}
    //                     style={{ width: '100%' }}
    //                     placeholder="Select relationship"
    //                     options={getRelationshipOptions()}
    //                 />
    //         )
    //     },
    //     createDateColumn('dob', 'Date of Birth'),
    //     {
    //         ...createTableColumn('share', 'Total amount or share of accumulations', 'Share'),
    //         title: <span style={{ whiteSpace: 'normal' }}>
    //             Total amount or share of accumulations in<br />Provident Funds to be paid to each nominee
    //         </span>
    //     },
    //     {
    //         ...createTableColumn('guardian', 'If the nominee is minor', 'Guardian'),
    //         title: <span style={{ whiteSpace: 'normal' }}>
    //             If the nominee is minor name and address<br />of the guardian who may receive the amount
    //         </span>
    //     }
    // ];

  const epfNomineeColumns = [
    createTableColumn('name', 'Name of the Nominee', 'Name'),
    createTableColumn('address', 'Address', 'Address'),
    {
        ...createTableColumn('relationship', 'Relationship', 'Relationship'),
        render: (text, record) => (
            isAllSectionsCompleted() ?
                <div>{text || '-'}</div> :
                <Select
                    value={text}
                    onChange={(value) => handleEpfNomineeChange(record.key, 'relationship', value)}
                    style={{ width: '100%' }}
                    placeholder="Select relationship"
                    options={getRelationshipOptions()}
                />
        )
    },
    createDateColumn('dob', 'Date of Birth'),
    {
        ...createTableColumn('share', 'Percentage Share', 'Percentage'),
        title: 'Percentage Share',
        render: (text, record) => (
            isAllSectionsCompleted() ?
                <div>{text || '-'}</div> :
                <Input
                    value={text}
                    onChange={(e) => {
                        const value = e.target.value;
                        // Allow only numbers and decimal point
                        if (/^\d*\.?\d*$/.test(value) || value === '') {
                            // Calculate current total percentage excluding current row
                            const currentTotal = epfNomineeData.reduce((sum, item) => {
                                if (item.key !== record.key) {
                                    const share = parseFloat(item.share) || 0;
                                    return sum + share;
                                }
                                return sum;
                            }, 0);
                            
                            const newShare = parseFloat(value) || 0;
                            const newTotal = currentTotal + newShare;
                            
                            if (newTotal <= 100) {
                                handleEpfNomineeChange(record.key, 'share', value);
                            } else {
                                message.warning(`Cannot exceed 100%. Current total: ${currentTotal}%`);
                            }
                        }
                    }}
                    onBlur={(e) => {
                        // Validate total percentage when user leaves the field
                        if (!validateTotalPercentage()) {
                            const currentTotal = epfNomineeData.reduce((sum, item) => {
                                const share = parseFloat(item.share) || 0;
                                return sum + share;
                            }, 0);
                            message.warning(`Total percentage must be exactly 100%. Current total: ${currentTotal}%`);
                        }
                    }}
                    placeholder="Percentage"
                    suffix="%"
                />
        )
    },
    {
        ...createTableColumn('guardian', 'If the nominee is minor', 'Guardian'),
        title: <span style={{ whiteSpace: 'normal' }}>
            If the nominee is a minor, name & relationship & address<br />of the guardian who may receive the amount during the minority of nominee
        </span>,
        render: (text, record) => (
            isAllSectionsCompleted() ?
                <div>{text || '-'}</div> :
                <Input
                    value={text}
                    onChange={(e) => {
                        const value = e.target.value;
                        if (/^[a-zA-Z\s.,'"\-:;!?()@#%&*+/=]*$/.test(value)) {
                            handleEpfNomineeChange(record.key, 'guardian', value);
                        }
                    }}
                    placeholder="Guardian name, relationship & address"
                />
        )
    }
];

    const epsFamilyColumns = [
        {
            title: 'Sr. No',
            dataIndex: 'key',
            key: 'sno',
            render: (_, __, index) => index + 1,
            width: 80,
        },
        createTableColumn('name', 'Name (As Per Aadhaar)', 'Name'),
        createTableColumn('address', 'Address (As Per Aadhaar)', 'Address'),
        createDateColumn('dob', 'Date Of Birth (As Per Aadhaar)'),
        createTableColumn('relationship', 'Relationship with the member', 'Relationship')
    ];

    const epsNomineeColumns = [
        createTableColumn('nomineeNameAddress', 'Name and Address of the nominee (As Per Aadhaar)', 'Name & Address'),
        createDateColumn('nomineeDob', 'Date of Birth'),
        {
            ...createTableColumn('nomineeRelationship', 'Relationship with member', 'Relationship'),
            render: (text, record) => (
                isAllSectionsCompleted() ?
                    <div>{text || '-'}</div> :
                    <Select
                        value={text}
                        onChange={(value) => handleEpsNomineeChange(record.key, 'nomineeRelationship', value)}
                        style={{ width: '100%' }}
                        placeholder="Select relationship"
                        options={getRelationshipOptions()}
                    />
            )
        }
    ];

    const gratuityNomineeColumns = [
        createTableColumn('name', 'Name with Address (As Per Aadhaar)', 'Name with Address'),
        {
            ...createTableColumn('relationship', 'Relationship', 'Relationship'),
            render: (text, record) => (
                isAllSectionsCompleted() ?
                    <div>{text || '-'}</div> :
                    <Select
                        value={text}
                        onChange={(value) => handleGratuityNomineeChange(record.key, 'relationship', value)}
                        style={{ width: '100%' }}
                        placeholder="Select relationship"
                        options={getRelationshipOptions()}
                    />
            )
        },
        createTableColumn('age', 'Age (As Per Aadhaar)', 'Age'),
        createTableColumn('proportion', 'Proportion', 'Proportion')
    ];

    const processedEpfNomineeData = epfNomineeData.map(item => ({ ...item, tableType: 'epfNomineeData' }));
    const processedEpsFamilyData = epsFamilyData.map(item => ({ ...item, tableType: 'epsFamilyData' }));
    const processedEpsNomineeData = epsNomineeData.map(item => ({ ...item, tableType: 'epsNomineeData' }));
    const processedGratuityNomineeData = gratuityNomineeData.map(item => ({ ...item, tableType: 'gratuityNomineeData' }));

    const ResetSectionsButton = ({ type, jwtBearer, user_id }) => {
        const dispatch = useDispatch();
        const [loading, setLoading] = useState(false);
        
        const handleResetSections = async () => {
            try {
                setLoading(true);
                
                const updates = {
                    is_section1_completed: false,
                    is_section2_completed: false,
                    is_section3_completed: false,
                    is_section4_completed: false,
                    is_section5_completed: false
                };
                
                const resultAction = await dispatch(
                    updateSectionStatus({
                        jwtBearer,
                        user_id,
                        type,
                        updates
                    })
                );
        
                if (updateSectionStatus.fulfilled.match(resultAction)) {
                    trackFeatureAction("MainHrForm", "Compliance Form", "reset");
                    message.success(`All sections reset successfully`);
                    await dispatch(getComplianceInfo({ jwtBearer, user_id }));
                } else {
                    throw new Error(resultAction.payload?.message || 'Failed to reset sections');
                }
            } catch (error) {
                message.error(error.message || 'Error resetting sections');
            } finally {
                setLoading(false);
            }
        };
        
        return (
            <Button 
                type="primary" 
                danger
                onClick={handleResetSections}
                loading={loading}
                style={{ marginLeft: 'auto' }}
            >
                Reset All Sections
            </Button>
        );
    };
    
    return (
        <EmployeesContainer>
            <MainCard
                title={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <span style={{ margin: 0 }}>EPF, EPS & Gratuity Nomination Forms</span>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "18px", alignItems: "center" }}>
                        <PdfCompliance formData={mainData} jwt_payload_group={jwt_payload_group} jwtBearer={jwtBearer} />
                        {(jwt_payload_group === '10' || jwt_payload_group === '14' || jwt_payload_group === '18') && 
                            <ResetSectionsButton 
                                type="compliance" 
                                jwtBearer={jwtBearer} 
                                user_id={user_id} 
                            />
                        }
                    </div>
                </div>}
                bordered={false}
            >
                <CustomCollapse
                    accordion
                    activeKey={activeKey}
                    onChange={onCollapseChange}
                    expandIcon={({ isActive }) => <FaCaretRight style={{ transform: `rotate(${isActive ? 90 : 0}deg)`, transition: 'transform 0.3s' }} />}
                >
                    <Panel
                        header={
                            <PanelHeader>
                                <PanelIcon><FaFileAlt /></PanelIcon>
                                <span>EPF Nomination Form</span>
                                {isSectionCompleted(1) && <Text type="success" style={{ marginLeft: 'auto' }}>Completed</Text>}
                            </PanelHeader>
                        }
                        key="1"
                    >
                        <SectionForm
                            form={sectionForms[0]}
                            layout="vertical"
                            onFinish={(values) => handleSectionSubmit(1, values)}
                            onValuesChange={(changedValues) => {
                                if ('maritalStatus' in changedValues) {
                                    handleMaritalStatusChange(changedValues.maritalStatus);
                                }
                            }}
                            autoComplete="off"
                        >
                            <Title level={4}>NOMINATION AND DECLARATION FORM FOR UNEXEMPTED/EXEMPTED ESTABLISHMENTS</Title>
                            <Text>Declaration and Nomination Form under the Employee Provident Funds and Employee Pension Schemes</Text>

                            <FormDivider orientation="left" orientationMargin="0">
                                Member's Details
                            </FormDivider>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <Form.Item
                                    label="1. Name (IN BLOCK LETTERS) (As Per Aadhaar)"
                                    name="name"
                                    rules={[{ required: true, message: 'Please enter your name' }, {
                                        pattern: '^[A-Z ]+$', message: 'Please enter name in block letters!'
                                    }]}
                                >
                                    {renderField(1,
                                        <Input placeholder="Full Name in Block Letters" />
                                    )}
                                </Form.Item>

                             

                                <Form.Item
                                    label="2. Date of Birth"
                                    name="dob"
                                    rules={[
                                        { required: true, message: 'Please select date of birth' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value) {
                                                    return Promise.resolve();
                                                }
                                                const currentDate = new Date();
                                                if (value.isAfter(currentDate, 'day')) {
                                                    return Promise.reject(new Error('Date of Birth cannot be in the future'));
                                                }
                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    {renderField(1,
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format="DD/MM/YYYY"
                                            placeholder="DD/MM/YYYY"
                                        />
                                    )}
                                </Form.Item>
                            </div>

                            <Form.Item
                                label="Select Father or Husband"
                                name="isFatherHusband"
                                rules={[{ required: true, message: 'Please select Father or Husband!' }]}
                            >
                                {renderField(
                                    1,
                                    <Radio.Group 
                                        onChange={(e) => {
                                            setIsFatherHusband(e.target.value);
                                            sectionForms[0].setFieldsValue({ isFatherHusband: e.target.value });
                                        }}
                                    >
                                        <Radio value="Father">Father</Radio>
                                        <Radio value="Husband">Husband</Radio>
                                    </Radio.Group>
                                )}
                            </Form.Item>
                            
                            {isFatherHusband === 'Father' ? (
                                <Form.Item
                                    label="Father's Name (As Per Aadhaar)"
                                    name="fatherName1"
                                    rules={[
                                        { required: true, message: "Please enter father's name" },
                                        { pattern: /^[A-Z ]+$/, message: 'Please enter name in block letters!' }
                                    ]}
                                >
                                    {renderField(1, <Input placeholder="Father's Name" />)}
                                </Form.Item>
                            ) : isFatherHusband === 'Husband' ? (
                                <Form.Item
                                    label="Husband's Name (As Per Aadhaar)"
                                    name="fatherName1"
                                    rules={[
                                        { required: true, message: "Please enter husband's name" },
                                        { pattern: /^[A-Z ]+$/, message: 'Please enter name in block letters!' }
                                    ]}
                                >
                                    {renderField(1, <Input placeholder="Husband's Name" />)}
                                </Form.Item>
                            ) : null}

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                                <Form.Item
                                    label="3. Account No. (UAN)"
                                    name="Kyc"
                                    rules={[
                                        { required: true, message: 'Please enter account number' },
                                        { pattern: /^\d{12}$/, message: 'UAN must be a 12-digit number' }
                                    ]}
                                >
                                    {renderField(1, <Input placeholder="Account Number"  />)}
                                </Form.Item>

                                <Form.Item
                                    label="4. Sex"
                                    name="gender"
                                    rules={[{ required: true, message: 'Please select your gender!' }]}
                                >
                                    {renderField(
                                        1,
                                        <Radio.Group>
                                            <Radio value="Male">Male</Radio>
                                            <Radio value="Female">Female</Radio>
                                        </Radio.Group>
                                    )}
                                </Form.Item>
                            </div>

                            <Form.Item
                                label="5. Marital Status"
                                name="maritalStatus"
                                rules={[{ required: true, message: 'Please select marital status' }]}
                            >
                                {renderField(1,
                                    <Select
                                        placeholder="Select Marital Status"
                                        onChange={handleMaritalStatusChange}
                                    >
                                        <Option value="Unmarried">Unmarried</Option>
                                        <Option value="Married">Married</Option>
                                        <Option value="Widow">Widow</Option>
                                        <Option value="Widower">Widower</Option>
                                        <Option value="Divorcee">Divorcee</Option>
                                    </Select>
                                )}
                            </Form.Item>

                            {(currentMaritalStatus === 'Married' || currentMaritalStatus === 'Widow' || currentMaritalStatus === 'Widower') && (
                                <Form.Item
                                    label="Spouse's Name (As Per Aadhaar)"
                                    name="spouseName1"
                                    rules={[{ pattern: NAME_REGEX, message: 'Please enter valid name!' }, { required: true, message: 'Please enter your spouce name!' }]}
                                >
                                    {renderField(1,
                                        <Input placeholder="Spouse's Name" />
                                    )}
                                </Form.Item>
                            )}

                            <Form.Item
                                label="6. Temporary Address"
                                name="addressTemp"
                                rules={[{ required: true, message: 'Please enter temporary address' }]}
                            >
                                {renderField(1,
                                    <Input.TextArea placeholder="Temporary Address" rows={3} />
                                )}
                            </Form.Item>
                            <Form.Item
                                label="6. Permanent Address"
                                name="addressPerma"
                                rules={[{ required: true, message: 'Please enter permanent address' }]}
                            >
                                {renderField(1,
                                    <Input.TextArea placeholder="Permanent Address" rows={3} />
                                )}
                            </Form.Item>

                              <Form.Item
                                label="7. Date of Joining"
                                name="dojDate"
                                rules={[{ required: true, message: 'Please select DOJ date' }]}
                            >
                                {renderField(3,
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                        placeholder="DD/MM/YYYY"
                                    />
                                )}
                            </Form.Item>

                            <FormDivider orientation="left" orientationMargin="0">
                                EPF Nomination Details (Part A)
                            </FormDivider>

                            {(currentMaritalStatus === 'Unmarried' || currentMaritalStatus === 'Widow' || currentMaritalStatus === 'Widower') && (
                                <Form.Item>
                                    <Checkbox
                                        checked={parentsDeceased}
                                        onChange={(e) => setParentsDeceased(e.target.checked)}
                                        disabled={isAllSectionsCompleted()}
                                    >
                                        My parents are deceased (Affidavit and death certificate will be required)
                                    </Checkbox>
                                </Form.Item>
                            )}

                            <Text>
                                I hereby nominate the person(s) mentioned below to receive the amount standing in my credit in the Employee Provident Fund, in the event of my death.
                            </Text>

                            <StyledTable
                                columns={epfNomineeColumns}
                                dataSource={processedEpfNomineeData}
                                pagination={false}
                                bordered
                                size="small"
                                style={{ margin: '16px 0' }}
                            />

                            {!isAllSectionsCompleted() && (
                                <Button type="dashed" onClick={addEpfNomineeRow} style={{ marginBottom: 16 }}>
                                    Add Another Nominee
                                </Button>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <Form.Item
                                    name="noFamilyDeclaration"
                                    valuePropName="checked"
                                >
                                    {renderField(1,
                                        <Checkbox>
                                            Certified that I have no family as defined in para 2 (g) of the Employee Provident Fund Scheme 1952 and should I acquire a family hereafter the above nomination should be deemed as invalid.
                                        </Checkbox>
                                    )}
                                </Form.Item>

                                {currentMaritalStatus === 'Married' && (
                                    <Form.Item
                                        name="dependentParents"
                                        valuePropName="checked"
                                    >
                                        {renderField(1,
                                            <Checkbox>
                                                Certified that my father/mother is/are dependent upon me.
                                            </Checkbox>
                                        )}
                                    </Form.Item>
                                )}
                            </div>

                            {!isAllSectionsCompleted() && (
                                <Form.Item>
                                    <SubmitButton
                                        type="primary"
                                        htmlType="submit"
                                        loading={loadingStates.section1}
                                    >
                                        {isSectionCompleted(1) ? 'Update EPF Nomination' : 'Save EPF Nomination'}
                                    </SubmitButton>
                                </Form.Item>
                            )}
                        </SectionForm>
                    </Panel>

                    <Panel
                        header={
                            <PanelHeader>
                                <PanelIcon><FaUser /></PanelIcon>
                                <span>EPS Family Particulars</span>
                                {isSectionCompleted(2) && <Text type="success" style={{ marginLeft: 'auto' }}>Completed</Text>}
                            </PanelHeader>
                        }
                        key="2"
                    >
                        <SectionForm
                            form={sectionForms[1]}
                            layout="vertical"
                            onFinish={(values) => handleSectionSubmit(2, values)}
                            autoComplete="off"
                        >
                            <Title level={4}>PART - (EPS) Para 18</Title>

                            {currentMaritalStatus === 'Unmarried' ? (
                                <>
                                    <Text>
                                        Certified that I have no family as defined in para 2 (vii) of the Employees' Family Pension Scheme 1995 and should I acquire a family hereafter I shall furnish Particulars thereon in the above form.
                                    </Text>
                                </>
                            ) : (
                                <Text>
                                    I hereby furnish below particulars of the members of my family who would be eligible to receive Widow/Children Pension in the event of my premature death in service.
                                </Text>
                            )}

                            {currentMaritalStatus !== 'Unmarried' && (
                                <>
                                    <StyledTable
                                        columns={epsFamilyColumns}
                                        dataSource={processedEpsFamilyData}
                                        pagination={false}
                                        bordered
                                        size="small"
                                        style={{ margin: '16px 0' }}
                                    />

                                    {!isAllSectionsCompleted() && (
                                        <Button type="dashed" onClick={addEpsFamilyRow} style={{ marginBottom: 16 }}>
                                            Add Family Member
                                        </Button>
                                    )}
                                </>
                            )}

                            {currentMaritalStatus === 'Unmarried' && (
                                <>
                                    <Text style={{ marginTop: 16 }}>
                                        I hereby nominate the following person for receiving the monthly widow pension (admissible under para 16 2 (a) (i) & (ii) in the event of my death without leaving any eligible family member for receiving pension.
                                    </Text>

                                    <StyledTable
                                        columns={epsNomineeColumns}
                                        dataSource={processedEpsNomineeData}
                                        pagination={false}
                                        bordered
                                        size="small"
                                        style={{ margin: '16px 0' }}
                                    />

                                    {/* {!isAllSectionsCompleted() && (
                                        <Button type="dashed" onClick={addEpsNomineeRow} style={{ marginBottom: 16 }}>
                                            Add Another Nominee
                                        </Button>
                                    )} */}
                                </>
                            )}

                       <Form.Item label="Name & address of the Factory/Establishment">
  <Form.Item
    name="establishmentNameAddress"
    noStyle
    rules={[
      { required: true, message: 'Please enter establishment details' },
    ]}
    initialValue="Ziel Financial Technologies Pvt Ltd, First Floor, Time Square building, Sushant Lok Phase-1, Gurugram, Haryana-122009"
  >
 {renderField(2,
      <Select placeholder="Select establishment">
        <Option value="Ziel Financial Technologies Pvt Ltd, First Floor, Time Square building, Sushant Lok Phase-1, Gurugram, Haryana-122009">Ziel Financial Technologies Pvt Ltd, First Floor, Time Square building, Sushant Lok Phase-1, Gurugram, Haryana-122009</Option>
        <Option value="Oxyzo Financial Services Limited, 1st Floor, TOWER-A, GLOBAL BUSINESS PARK, Mehrauli-Gurgaon Rd, Sikanderpur, Sector 26, Gurugram, Haryana 122002">Oxyzo Financial Services Limited, 1st Floor, TOWER-A, GLOBAL BUSINESS PARK, Mehrauli-Gurgaon Rd, Sikanderpur, Sector 26, Gurugram, Haryana 122002</Option>
      </Select>
    )}
  </Form.Item>
</Form.Item>

                            <Form.Item>
                                <Space>
                                    <Text>Joining Location (City):</Text>
                                    <Form.Item name="epsPlace" noStyle rules={[{ required: true, message: 'Please enter place' }]} >
                                        {renderField(2, <Input placeholder="Joining Location (City)"  />)}
                                    </Form.Item>
                                    <Text>Date:</Text>
                                    <Form.Item name="establishmentDate" noStyle rules={[{ required: true, message: 'Please select date' }]} >
                                        {renderField(2, <DatePicker placeholder="Date"  />)}
                                    </Form.Item>
                                </Space>
                            </Form.Item>
                            {/* <Form.Item>
                                <Space>
                                    <Text>destination:</Text>
                                    <Form.Item name="destination" noStyle rules={[{ required: true, message: 'Please enter destination' }]} >
                                        {renderField(2, <Input placeholder="destination"  />)}
                                    </Form.Item>
                                 
                                    
                                </Space>
                            </Form.Item> */}

                            {!isAllSectionsCompleted() && (
                                <Form.Item>
                                    <SubmitButton
                                        type="primary"
                                        htmlType="submit"
                                        loading={loadingStates.section2}
                                    >
                                        {isSectionCompleted(2) ? 'Update EPS Family Particulars' : 'Save EPS Family Particulars'}
                                    </SubmitButton>
                                </Form.Item>
                            )}
                        </SectionForm>
                    </Panel>

                    <Panel
                        header={
                            <PanelHeader>
                                <PanelIcon><FaFileAlt /></PanelIcon>
                                <span>Employee Statement</span>
                                {isSectionCompleted(3) && <Text type="success" style={{ marginLeft: 'auto' }}>Completed</Text>}
                            </PanelHeader>
                        }
                        key="3"
                    >
                        <SectionForm
                            form={sectionForms[2]}
                            layout="vertical"
                            onFinish={(values) => handleSectionSubmit(3, values)}
                            autoComplete="off"
                        >
                            <Title level={4}>Statement</Title>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <Form.Item
                                    label="1. Name of employee in full (As Per Aadhaar)"
                                    name="employeeName"
                                    rules={[{ required: true, message: 'Please enter employee name' }, { pattern: NAME_REGEX, message: 'Please enter a valid name!' }]}
                                >
                                    {renderField(3,
                                        <Input placeholder="Full Name" />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    label="2. Sex"
                                    name="statementGender"
                                    rules={[{ required: true, message: 'Please select gender' }]}
                                >
                                    {renderField(3,
                                        <Radio.Group>
                                            <Radio value="Male">Male</Radio>
                                            <Radio value="Female">Female</Radio>
                                        </Radio.Group>
                                    )}
                                </Form.Item>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <Form.Item
                                    label="3. Religion"
                                    name="religion"
                                    rules={[{ required: true, message: 'Please enter religion' }, { pattern: NAME_REGEX, message: 'Please enter a valid value!' }]}
                                >
                                    {renderField(3,
                                        <Input placeholder="Religion" />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    label="4. Marital Status"
                                    name="statementMaritalStatus"
                                    rules={[{ required: true, message: 'Please select marital status' }]}
                                >
                                    {renderField(3,
                                        <Select placeholder="Select Marital Status">
                                            <Option value="Unmarried">Unmarried</Option>
                                            <Option value="Married">Married</Option>
                                            <Option value="Widow">Widow</Option>
                                            <Option value="Widower">Widower</Option>
                                        </Select>
                                    )}
                                </Form.Item>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <Form.Item
                                    label="5. Department/Branch/Section"
                                    name="department"
                                    rules={[{ required: true, message: 'Please enter department' }, { pattern: ALPHA_NUMERIC_REGEX, message: 'Please enter valid value!' }]}
                                >
                                    {renderField(3,
                                        <Input placeholder="Department/Branch/Section"  />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    label="6. Post held with Ticket No.(Designation)"
                                    name="postWithTicketNo"
                                    rules={[{ required: true, message: 'Please enter post details' }, { pattern: ALPHA_NUMERIC_REGEX, message: 'Please enter valid value!' }]}
                                >
                                    {renderField(3,
                                        <Input placeholder="Post with Ticket No." />
                                    )}
                                </Form.Item>
                            </div>

                            <Form.Item
                                label="7. Date of Appointment"
                                name="appointmentDate"
                                rules={[{ required: true, message: 'Please select appointment date' }]}
                            >
                                {renderField(3,
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                        placeholder="DD/MM/YYYY"
                                    />
                                )}
                            </Form.Item>

                            <FormDivider orientation="left" orientationMargin="0">
                                8. Permanent Address
                            </FormDivider>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                                <Form.Item
                                    label="Village/City"
                                    name="village"
                                    rules={[{ required: true, message: 'Please enter village/city' }]}
                                >
                                    {renderField(3,
                                        <Input placeholder="Village" />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    label="Thana"
                                    name="thana"
                                    rules={[{ required: true, message: 'Please enter thana' }]}
                                >
                                    {renderField(3,
                                        <Input placeholder="Thana" />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    label="Sub-division"
                                    name="subDivision"
                                    rules={[{ required: true, message: 'Please enter sub-division' }]}
                                >
                                    {renderField(3,
                                        <Input placeholder="Sub-division" />
                                    )}
                                </Form.Item>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                                <Form.Item
                                    label="Post Office"
                                    name="postOffice"
                                    rules={[{ required: true, message: 'Please enter post office' }, { pattern: NAME_REGEX, message: 'Please enter valid post office name!' }]}
                                >
                                    {renderField(3,
                                        <Input placeholder="Post Office" />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    label="District"
                                    name="district"
                                    rules={[{ required: true, message: 'Please enter district' }, { pattern: NAME_REGEX, message: 'Please enter valid district name!' }]}
                                >
                                    {renderField(3,
                                        <Input placeholder="District" />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    label="State"
                                    name="state"
                                    rules={[{ required: true, message: 'Please enter state' }, { pattern: NAME_REGEX, message: 'Please enter valid state name!' }]}
                                >
                                    {renderField(3,
                                        <Input placeholder="State" />
                                    )}
                                </Form.Item>
                            </div>

                            <FormDivider orientation="left" orientationMargin="0">
                                Witness Details
                            </FormDivider>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <Form.Item
                                    label="Witness 1 Name & Address"
                                    name="witness1NameAddress"
                                    rules={[{ required: true, message: 'Please enter witness details' }]}
                                >
                                    {renderField(3,
                                        <Input.TextArea placeholder="Name and Address" rows={2} />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    label="Witness 2 Name & Address"
                                    name="witness2NameAddress"
                                    rules={[{ required: true, message: 'Please enter witness details' }]}
                                >
                                    {renderField(3,
                                        <Input.TextArea placeholder="Name and Address" rows={2} />
                                    )}
                                </Form.Item>
                            </div>

                            {!isAllSectionsCompleted() && (
                                <Form.Item>
                                    <SubmitButton
                                        type="primary"
                                        htmlType="submit"
                                        loading={loadingStates.section3}
                                    >
                                        {isSectionCompleted(3) ? 'Update Employee Statement' : 'Save Employee Statement'}
                                    </SubmitButton>
                                </Form.Item>
                            )}
                        </SectionForm>
                    </Panel>

                    <Panel
                        header={
                            <PanelHeader>
                                <PanelIcon><FaFileAlt /></PanelIcon>
                                <span>New Form No.11 - Declaration Form</span>
                                {isSectionCompleted(4) && <Text type="success" style={{ marginLeft: 'auto' }}>Completed</Text>}
                            </PanelHeader>
                        }
                        key="4"
                    >
                        <SectionForm
                            form={sectionForms[3]}
                            layout="vertical"
                            onFinish={(values) => handleSectionSubmit(4, values)}
                            autoComplete="off"
                        >
                            <Title level={4}>EMPLOYEES' PROVIDENT FUND ORGANISATION</Title>
                            <Text>Employees' Provident Funds Scheme, 1952 (Paragraph 34 & 61A) & Employees' Pension Scheme, 1995 (Paragraph 24)</Text>

                            <FormDivider orientation="left" orientationMargin="0">
                                Member Details
                            </FormDivider>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <Form.Item
                                    label="1. Name of the member (As Per Aadhaar)"
                                    name="memberName"
                                    rules={[{ required: true, message: 'Please enter member name' }, { pattern: NAME_REGEX, message: 'Please enter valid name!' }]}
                                >
                                    {renderField(4,
                                        <Input placeholder="Member Name" />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    label="2. Father's/Husband's Name (As per Aadhaar. If you are female, enter the name as mentioned on the back of your Aadhaar card.)"
                                    name="fatherHusbandName"
                                    rules={[{ required: true, message: "Please enter father/husband name" }, { pattern: '^[A-Z ]+$', message: 'Please enter name in block letters!' }]}
                                >
                                    {renderField(4,
                                        <Input placeholder="Father's/Husband's Name" />
                                    )}
                                </Form.Item>
                            </div>

                            {sectionForms[3].getFieldValue('form11MaritalStatus') === 'Married' && (
                                <Form.Item
                                    label="Spouse's Name (As Per Aadhaar)"
                                    name="spouseName"
                                    rules={[{ pattern: NAME_REGEX, message: 'Please enter valid name!' }, { required: true, message: 'Please enter Spouce' }]}
                                >
                                    {renderField(4,
                                        <Input placeholder="Spouse's Name" />
                                    )}
                                </Form.Item>
                            )}

                            <Form.Item
                                label="3. Date of Birth"
                                name="form11Dob"
                                rules={[
                                    { required: true, message: 'Please select date of birth' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value) {
                                                return Promise.resolve();
                                            }
                                            if (value.isAfter(new Date())) {
                                                return Promise.reject(new Error('Please select a valid Date of Birth'));
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                {renderField(4,
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                        placeholder="DD/MM/YYYY"
                                    />
                                )}
                            </Form.Item>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <Form.Item
                                    label="4. Gender"
                                    name="form11Gender"
                                    rules={[{ required: true, message: 'Please select gender' }]}
                                >
                                    {renderField(4,
                                        <Radio.Group>
                                            <Radio value="Male">Male</Radio>
                                            <Radio value="Female">Female</Radio>
                                            <Radio value="Transgender">Transgender</Radio>
                                        </Radio.Group>
                                    )}
                                </Form.Item>

                                <Form.Item
                                    label="5. Marital Status"
                                    name="form11MaritalStatus"
                                    rules={[{ required: true, message: 'Please select marital status' }]}
                                >
                                    {renderField(4,
                                        <Select placeholder="Select Marital Status">
                                            <Option value="Unmarried">Unmarried</Option>
                                            <Option value="Married">Married</Option>
                                            <Option value="Widow">Widow</Option>
                                            <Option value="Widower">Widower</Option>
                                            <Option value="Divorced">Divorced</Option>
                                        </Select>
                                    )}
                                </Form.Item>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <Form.Item
                                    label="6. (a) Email ID (Personal Email Id)"
                                    name="email"
                                    rules={[{ required: true, message: 'Please enter email' }, { type: 'email', message: 'Please enter a valid email' }]}
                                >
                                    {renderField(4,
                                        <Input placeholder="Email ID" />
                                    )}
                                </Form.Item>
                                <Form.Item
                                    label="6. (b) Email ID (Official)"
                                    name="officialEmail"
                                    rules={[{ required: true, message: 'Please enter email' }, { type: 'email', message: 'Please enter a valid email' }]}
                                >
                                    {renderField(4,
                                        <Input placeholder="Official Email ID" />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    label="6. (c) Mobile No."
                                    name="mobile"
                                    rules={[{ required: true, message: 'Please enter mobile number' }, {
                                        pattern: PHONE_REGEX,
                                        message: 'Please enter valid 10-digit mobile number!'
                                    }]}
                                >
                                    {renderField(4,
                                        <Input placeholder="Mobile No." />
                                    )}
                                </Form.Item>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                {/* <Form.Item
                                    label="7. Whether earlier a member of EPF Scheme, 1952"
                                    name="previousEPFMember"
                                    rules={[{ required: true, message: 'Please select option' }]}
                                >
                                    {renderField(4,
                                        <Radio.Group>
                                            <Radio value="Yes">Yes</Radio>
                                            <Radio value="No">No</Radio>
                                        </Radio.Group>
                                    )}
                                </Form.Item> */}

<Form.Item
    label="7. Whether earlier a member of EPF Scheme, 1952"
    name="previousEPFMember"
    rules={[{ required: true, message: 'Please select option' }]}
>
    {renderField(4,
        <Radio.Group onChange={handlePreviousEPFMemberChange}>
            <Radio value="Yes">Yes</Radio>
            <Radio value="No">No</Radio>
        </Radio.Group>
    )}
</Form.Item>

                                <Form.Item
                                    label="8. Whether earlier a member of EPS, 1995"
                                    name="previousEPSMember"
                                    rules={[{ required: true, message: 'Please select option' }]}
                                >
                                    {renderField(4,
                                        <Radio.Group>
                                            <Radio value="Yes">Yes</Radio>
                                            <Radio value="No">No</Radio>
                                        </Radio.Group>
                                    )}
                                </Form.Item>
                            </div>

                  {isPreviousEPFMember && (
  <>
    <FormDivider orientation="left" orientationMargin="0">
      Previous Employment Details (if any)
    </FormDivider>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
      <Form.Item
        label="a) Universal Account Number"
        name="universalKyc"
        rules={[
          { required: true, message: 'Please enter universal account number' },
          { pattern: /^\d{12}$/, message: 'UAN must be a 12-digit number' }
        ]}
      >
        {renderField(4,
          <Input placeholder="Previous Account No." />
        )}
      </Form.Item>

      <Form.Item
        label="b) Previous PF Account Number"
        name="previousPFKyc"
        rules={[
          { required: true, message: 'Please enter Previous PF account number' },
          { pattern: /^[A-Za-z0-9]{22}$/, message: 'Must be exactly 22 alphanumeric characters' }
        ]}
      >
        {renderField(4,
          <Input placeholder="Previous PF Account No." />
        )}
      </Form.Item>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
      <Form.Item
        label="c) Date of exit from previous employment"
        name="previousExitDate"
        // rules={[{ required: true, message: 'Please select exit date' }]}
      >
        {renderField(4,
          <DatePicker
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            placeholder="DD/MM/YYYY"
          />
        )}
      </Form.Item>

      <Form.Item
        label="d) Scheme Certificate No. (if issued)"
        name="schemeCertNo"
      >
        {renderField(4,
          <Input placeholder="Scheme Certificate No." />
        )}
      </Form.Item>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
      <Form.Item
        label="e) Pension Payment Order (PPO) No. (if issued)"
        name="ppoNo"
      >
        {renderField(4,
          <Input placeholder="PPO No." />
        )}
      </Form.Item>
    </div>
  </>
)}

                            <FormDivider orientation="left" orientationMargin="0">
                                International Worker Details
                            </FormDivider>

                            <Form.Item
                                label="a) International Worker"
                                name="internationalWorker"
                                rules={[{ required: true, message: 'Please select option' }]}
                            >
                                {renderField(4,
                                    <Radio.Group onChange={handleInternationalWorkerChange}>
                                        <Radio value="Yes">Yes</Radio>
                                        <Radio value="No">No</Radio>
                                    </Radio.Group>
                                )}
                            </Form.Item>

                            {isInternationalWorker && (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                        <Form.Item
                                            label="b) Country of origin (India/Name of other country)"
                                            name="countryOfOrigin"
                                            rules={[{ required: true, message: 'Please enter country of origin' }]}
                                        >
                                            {renderField(4,
                                                <Input placeholder="Country of origin" />
                                            )}
                                        </Form.Item>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                                        <Form.Item
                                            label="c) Passport No."
                                            name="passportNo"
                                        >
                                            {renderField(4,
                                                <Input placeholder="Passport Number" />
                                            )}
                                        </Form.Item>

                                        <Form.Item
                                            label="d) Passport Validity From"
                                            name="passportValidityFrom"
                                        >
                                            {renderField(4,
                                                <DatePicker
                                                    style={{ width: '100%' }}
                                                    format="DD/MM/YYYY"
                                                    placeholder="DD/MM/YYYY"
                                                />
                                            )}
                                        </Form.Item>

                                        <Form.Item
                                            label="e) Passport Validity To"
                                            name="passportValidityTo"
                                        >
                                            {renderField(4,
                                                <DatePicker
                                                    style={{ width: '100%' }}
                                                    format="DD/MM/YYYY"
                                                    placeholder="DD/MM/YYYY"
                                                />
                                            )}
                                        </Form.Item>
                                    </div>
                                </>
                            )}

                            <FormDivider orientation="left" orientationMargin="0">
                                KYC Details
                            </FormDivider>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <Form.Item
                                    label="a) Bank Account No."
                                    name="accountNoKyc"
                                    rules={[{ required: true, message: 'Please enter bank account no.' }, { pattern: NUMBER_REGEX, message: 'Please enter valid value!' }]}
                                >
                                    {renderField(4,
                                        <Input placeholder="Bank Account No." />
                                    )}
                                </Form.Item>
                                <Form.Item
                                    label="a) IFSC Code"
                                    name="ifscNoKyc"
                                    rules={[{ required: true, message: 'Please enter IFSC code' }, { pattern: IFSC_REGEX, message: 'Please enter valid value!' }]}
                                >
                                    {renderField(4,
                                        <Input placeholder="IFSC Code" />
                                    )}
                                </Form.Item>
                            </div>

                            <Form.Item
                                label="c) Permanent Account Number (PAN)"
                                name="pan"
                                rules={[{ required: true, message: 'Please enter PAN' }, { pattern: PAN_REGEX, message: 'Please enter a valid PAN number!' }]}
                            >
                                {renderField(4,
                                    <Input placeholder="PAN"  />
                                )}
                            </Form.Item>
                            <Form.Item
                                label="c) Aadhaar Number"
                                name="aadhaarNumber"
                                rules={[{ required: true, message: 'Please enter Aadhaar number' }, { pattern: AADHAAR_REGEX, message: 'Please enter a valid Aadhaar number!' }]}
                            >
                                {renderField(4,
                                    <Input placeholder="Aadhaar Number"  />
                                )}
                            </Form.Item>

                            {!isAllSectionsCompleted() && (
                                <Form.Item>
                                    <SubmitButton
                                        type="primary"
                                        htmlType="submit"
                                        loading={loadingStates.section4}
                                    >
                                        {isSectionCompleted(4) ? 'Update PF Details' : 'Save PF Details'}
                                    </SubmitButton>
                                </Form.Item>
                            )}
                        </SectionForm>
                    </Panel>

                    <Panel
                        header={
                            <PanelHeader>
                                <PanelIcon><FaFileAlt /></PanelIcon>
                                <span>Gratuity Nomination Form</span>
                                {isSectionCompleted(5) && <Text type="success" style={{ marginLeft: 'auto' }}>Completed</Text>}
                            </PanelHeader>
                        }
                        key="5"
                    >
                        <SectionForm
                            form={sectionForms[4]}
                            layout="vertical"
                            onFinish={(values) => handleSectionSubmit(5, values)}
                            autoComplete="off"
                        >
                            <Title level={4}>PAYMENT OF GRATUITY (CENTRAL) RULES FORM 'F'</Title>

                            <div style={{ marginBottom: 16 }}>
                                <Text>To,</Text>
                                <Form.Item name="establishmentName" initialValue="Ziel Financial Technologies Pvt Ltd, First Floor, Time Square building, Sushant Lok Phase-1, Gurugram, Haryana-122009" rules={[{ required: true, message: 'Please enter establishment name' }]}
                                >
                             {renderField(5,
      <Select placeholder="Select establishment">
        <Option value="Ziel Financial Technologies Pvt Ltd, First Floor, Time Square building, Sushant Lok Phase-1, Gurugram, Haryana-122009">Ziel Financial Technologies Pvt Ltd, First Floor, Time Square building, Sushant Lok Phase-1, Gurugram, Haryana-122009</Option>
        <Option value="Oxyzo Financial Services Limited, 1st Floor, TOWER-A, GLOBAL BUSINESS PARK, Mehrauli-Gurgaon Rd, Sikanderpur, Sector 26, Gurugram, Haryana 122002">Oxyzo Financial Services Limited, 1st Floor, TOWER-A, GLOBAL BUSINESS PARK, Mehrauli-Gurgaon Rd, Sikanderpur, Sector 26, Gurugram, Haryana 122002</Option>
      </Select>
    )}
                                </Form.Item>
                            </div>

                            <FormDivider orientation="left" orientationMargin="0">
                                Nomination
                            </FormDivider>

                            <Text>
                                I, <Form.Item name="nominatorName" style={{ display: 'inline-block', width: 300, margin: '0 8px' }} rules={[{ required: true, message: 'Please enter nominator name' }, { pattern: NAME_REGEX, message: 'Please enter a valid name!' }]}>
                                    {renderField(5,
                                        <Input placeholder="Name in full here (As Per Aadhaar)" />
                                    )}
                                </Form.Item> whose particulars are given in the statement below, hereby nominate the person(s) mentioned below to receive the gratuity payable after my death.
                            </Text>

                            <StyledTable
                                columns={gratuityNomineeColumns}
                                dataSource={processedGratuityNomineeData}
                                pagination={false}
                                bordered
                                size="small"
                                style={{ margin: '16px 0' }}
                            />

                            {/* {!isAllSectionsCompleted() && (
                                <Button type="dashed" onClick={addGratuityNomineeRow} style={{ marginBottom: 16 }}>
                                    Add Another Nominee
                                </Button>
                            )} */}

                            <div style={{ margin: '16px 0' }}>
                                <Form.Item
                                    name="gratuityDeclaration1"
                                    valuePropName="checked"
                                    rules={[{ required: true, message: 'Please accept the declaration' }]}
                                >
                                    {renderField(5,
                                        <Checkbox>
                                            2. Thereby certify that the person(s) mentioned is/are a member(s) of my family within the meaning of clause (h) of Section 2 of the Payment of Gratuity Act, 1972.
                                        </Checkbox>
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="gratuityDeclaration2"
                                    valuePropName="checked"
                                >
                                    {renderField(5,
                                        <Checkbox>
                                            3. Thereby declare that I have no family within the meaning of clause (h) of Section 2 of the said Act.
                                        </Checkbox>
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="gratuityDeclaration3"
                                    valuePropName="checked"
                                >
                                    {renderField(5,
                                        <Checkbox>
                                            4. (a) My father/mother/parents is/are not dependent on me.
                                        </Checkbox>
                                    )}
                                </Form.Item>

                                <Form.Item
                                    name="gratuityDeclaration4"
                                    valuePropName="checked"
                                >
                                    {renderField(5,
                                        <Checkbox>
                                            4. (b) My husband's father/mother/parents is/are not dependent on my husband.
                                        </Checkbox>
                                    )}
                                </Form.Item>

                                 <Form.Item
                                    name="gratuityDeclaration5"
                                    valuePropName="checked"
                                >
                                    {renderField(5,
                                        <Checkbox>
                                            5. I have excluded my husband from my family by a notice dated the
                                            {/* <Form.Item name="exclusionNoticeDate" style={{ display: 'inline-block', width: 150, margin: '0 8px' }}>
                                                {renderField(5,
                                                    <DatePicker
                                                        format="DD/MM/YYYY"
                                                        style={{ width: '100%' }}
                                                    />
                                                )}
                                            </Form.Item> */}
                                            to the controlling authority in terms of the proviso to clause (h) of Section 2 of the said Act.
                                        </Checkbox>
                                    )}
                                </Form.Item> 

                                <Form.Item
                                    name="gratuityDeclaration6"
                                    valuePropName="checked"
                                    rules={[{ required: true, message: 'Please accept the declaration' }]}
                                >
                                    {renderField(5,
                                        <Checkbox>
                                            5. Nomination made herein invalidates my previous nomination.
                                        </Checkbox>
                                    )}
                                </Form.Item>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: 16 }}>
                                <Form.Item
                                    label="Acknowledgment Date"
                                    name="acknowledgmentDate"
                                    rules={[{ required: true, message: 'Please select date' }]}
                                >
                                    {renderField(5,
                                        <DatePicker
                                            style={{ width: '100%' }}
                                            format="DD/MM/YYYY"
                                            placeholder="DD/MM/YYYY"
                                        />
                                    )}
                                </Form.Item>

                                <Form.Item
                                    label="Employee Signature"
                                    name="employeeSignature"
                                    rules={[{ required: true, message: 'Please enter signature' }]}
                                >
                                    {renderField(5,
                                        <Input placeholder="Employee Signature" />
                                    )}
                                </Form.Item>
                            </div>

                            {!isAllSectionsCompleted() && (
                                <Form.Item>
                                    <SubmitButton
                                        type="primary"
                                        htmlType="submit"
                                        loading={loadingStates.section5}
                                    >
                                        {isSectionCompleted(5) ? 'Update Gratuity Nomination' : 'Save Gratuity Nomination'}
                                    </SubmitButton>
                                </Form.Item>
                            )}
                        </SectionForm>
                    </Panel>
                </CustomCollapse>
            </MainCard>
        </EmployeesContainer>
    );
})

export default Compliance;